import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, User } from 'lucide-react';
import { SmartImage } from '../components/ArticleCard';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import ShareButtons from '../components/ShareButtons';
import RelatedArticles from '../components/RelatedArticles';
import { useStoryBySlug, normaliseStory } from '../hooks/useStories';

const ArticleSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-3 w-40 bg-[#efe8de] mb-4" />
    <div className="h-6 w-24 bg-[#efe8de] mb-4" />
    <div className="h-10 w-full bg-[#efe8de] mb-3" />
    <div className="h-10 w-3/4 bg-[#efe8de] mb-6" />
    <div className="aspect-[16/9] w-full bg-[#efe8de] mb-6" />
    <div className="space-y-3">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="h-4 bg-[#efe8de]" style={{ width: `${60 + (i * 3) % 40}%` }} />
      ))}
    </div>
  </div>
);

const Article = () => {
  const { slug } = useParams();
  const { data: raw, loading, error } = useStoryBySlug(slug);
  const post = normaliseStory(raw);

  const breadcrumbItems = post
    ? [
        { name: 'Accueil', to: '/' },
        { name: post.category, to: `/category/${post.categorySlug || post.category_slug}` },
        { name: post.title },
      ]
    : [{ name: 'Accueil', to: '/' }, { name: 'Article' }];

  return (
    <main className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
      {post && (
        <SEO
          title={post.title}
          description={post.excerpt}
          image={post.image}
          url={`/article/${post.slug}`}
          type="article"
          author={post.author}
          publishedTime={post.created_at}
          keywords={`${post.category}, ${post.title.split(' ').slice(0, 5).join(' ')}, BcoolStore`}
        />
      )}
      {!post && !loading && (
        <SEO
          title="Article introuvable"
          description="Cet article a été déplacé ou supprimé."
          url={`/article/${slug}`}
          noIndex
        />
      )}

      {/* Fil d'ariane */}
      <Breadcrumb items={breadcrumbItems} className="mb-6" />

      <Link
        to="/"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#7a716c] hover:text-[#8b2c3f] transition-colors mb-6"
      >
        <ArrowLeft size={13} /> Retour à l&apos;accueil
      </Link>

      {loading && <ArticleSkeleton />}

      {error && !loading && (
        <div className="py-16 text-center">
          <p className="text-2xl font-bold text-[#1a1414] mb-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            Article introuvable
          </p>
          <p className="text-[#7a716c] mb-6">Cet article a peut-être été déplacé ou supprimé.</p>
          <Link
            to="/"
            className="inline-block border border-[#1a1414] text-[#1a1414] text-[11px] font-semibold uppercase tracking-[0.22em] px-8 py-3 hover:bg-[#1a1414] hover:text-white transition-colors"
          >
            Retour à l&apos;accueil
          </Link>
        </div>
      )}

      {!loading && !error && post && (
        <article>
          {/* Header article */}
          <header className="mb-8">
            <Link
              to={`/category/${post.categorySlug || post.category_slug}`}
              className="inline-block bg-[#8b2c3f] hover:bg-[#6d1f2f] text-white text-[10px] font-bold uppercase tracking-[0.22em] px-3 py-1.5 mb-5 transition-colors"
            >
              {post.category}
            </Link>
            <h1
              className="text-[30px] md:text-[42px] font-bold text-[#1a1414] leading-[1.1] mb-5"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {post.title}
            </h1>
            <p
              className="text-[18px] md:text-[20px] text-[#3a3232] italic leading-[1.55] mb-6"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              {post.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-5 text-[12px] text-[#7a716c] uppercase tracking-[0.12em] pt-4 border-t border-[#e6ddd0]">
              <span className="inline-flex items-center gap-1.5">
                <User size={12} /> Par <span className="text-[#1a1414] font-semibold normal-case tracking-normal">{post.author || 'Bcool'}</span>
              </span>
              <span className="text-[#c9a961]">·</span>
              <span>{post.date || ''}</span>
              <span className="text-[#c9a961]">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={12} /> {post.read_time || post.readTime || '10 min de lecture'}
              </span>
            </div>
          </header>

          {/* Image principale */}
          <div className="relative aspect-[16/9] overflow-hidden bg-[#efe8de] mb-10">
            <SmartImage src={post.image} alt={post.title} hover={false} />
          </div>

          {/* Corps de l'article */}
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: post.body || '' }}
          />

          {/* Boutons partage social */}
          <ShareButtons
            title={post.title}
            url={`/article/${post.slug}`}
            className="mt-12"
          />

          {/* Bloc "signature auteur" */}
          <div className="mt-8 flex items-start gap-4 bg-white border border-[#e6ddd0] p-5">
            <div className="w-14 h-14 rounded-full bg-[#c9a961]/20 text-[#8b2c3f] flex items-center justify-center flex-shrink-0 font-bold text-xl italic" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              B
            </div>
            <div>
              <div className="text-[12px] uppercase tracking-[0.2em] text-[#c9a961] font-bold mb-1">Récit signé</div>
              <div className="text-[16px] font-bold text-[#1a1414]">
                {post.author || 'Bcool'}
              </div>
              <p className="text-[13px] text-[#7a716c] mt-1 leading-relaxed">
                La plume derrière les histoires de BcoolStore. Récits vécus, chroniques et confidences,
                publiés avec soin.
              </p>
            </div>
          </div>

          {/* Articles similaires */}
          <RelatedArticles
            currentSlug={post.slug}
            categorySlug={post.categorySlug || post.category_slug}
            limit={3}
          />
        </article>
      )}
    </main>
  );
};

export default Article;
